"use client";

import { useEffect, useRef, useState } from "react";
import Image from "next/image";
import Link from "next/link";
import { Search, X, Loader2 } from "lucide-react";
import { resolveMediaUrl } from "@/lib/media";
import type { SearchResult } from "@/lib/dto";

type Props = {
  packagesPath: string;
  onNavigate?: () => void;
};

const DEBOUNCE_MS = 500;

/**
 * As-you-type package search overlay — mirrors production's KeySearch
 * (website/src/components/KeySearch/KeySearch.js): debounced fetch, a
 * result list with thumbnail/duration/review-count, dismissible via a
 * backdrop click. Calls /api/search (a thin wrapper around the server-only
 * apiSearch()) rather than the legacy /filterpackages endpoint.
 */
export function SearchOverlay({ packagesPath, onNavigate }: Props) {
  const [open, setOpen] = useState(false);
  const [query, setQuery] = useState("");
  const [pending, setPending] = useState(false);
  const [result, setResult] = useState<SearchResult | null>(null);
  const inputRef = useRef<HTMLInputElement>(null);

  useEffect(() => {
    if (open) inputRef.current?.focus();
  }, [open]);

  useEffect(() => {
    const trimmed = query.trim();
    if (!trimmed) {
      const id = requestAnimationFrame(() => {
        setResult(null);
        setPending(false);
      });
      return () => cancelAnimationFrame(id);
    }
    const pendingId = requestAnimationFrame(() => setPending(true));
    const timer = setTimeout(async () => {
      try {
        // trailingSlash: true redirects /api/search -> /api/search/; fetch the
        // trailing-slash form directly rather than eating a 308 round-trip on
        // every keystroke (see docs/configuration.md's trailingSlash note).
        const res = await fetch(
          `/api/search/?q=${encodeURIComponent(trimmed)}`,
        );
        const data: SearchResult = await res.json();
        setResult(data);
      } catch {
        setResult(null);
      } finally {
        setPending(false);
      }
    }, DEBOUNCE_MS);
    return () => {
      cancelAnimationFrame(pendingId);
      clearTimeout(timer);
    };
  }, [query]);

  useEffect(() => {
    if (!open) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key === "Escape") setOpen(false);
    };
    window.addEventListener("keydown", onKey);
    return () => window.removeEventListener("keydown", onKey);
  }, [open]);

  const close = () => {
    setOpen(false);
    setQuery("");
    setResult(null);
  };

  return (
    <>
      <button
        type="button"
        aria-label="Search packages"
        onClick={() => setOpen(true)}
        className="transition-opacity"
      >
        <svg className="h-6 w-6" aria-hidden>
          <use xlinkHref="/icons.svg#search"></use>
        </svg>
      </button>

      {open ? (
        <div className="fixed inset-0 z-[60]">
          <button
            type="button"
            aria-label="Close search"
            onClick={close}
            className="absolute inset-0 bg-headings/50 backdrop-blur-md"
          />
          <div className="relative z-10 mx-auto mt-24 w-[min(92vw,40rem)] rounded-2xl bg-paper shadow-2xl">
            <div className="flex items-center gap-3 border-b border-line px-5 py-4">
              <Search
                size={18}
                className="shrink-0 text-muted-soft"
                aria-hidden
              />
              <input
                ref={inputRef}
                type="text"
                value={query}
                onChange={(e) => setQuery(e.target.value)}
                placeholder="Find your trip.."
                className="w-full border-0 bg-transparent text-base text-headings outline-none placeholder:text-muted-soft"
              />
              {pending ? (
                <Loader2
                  size={18}
                  className="shrink-0 animate-spin text-primary"
                  aria-hidden
                />
              ) : null}
              <button
                type="button"
                aria-label="Close search"
                onClick={close}
                className="shrink-0 text-muted-soft hover:text-headings"
              >
                <X size={18} aria-hidden />
              </button>
            </div>

            {query.trim() && !pending ? (
              <div className="max-h-[60vh] overflow-y-auto p-3">
                {result?.packages.length ? (
                  <ul className="space-y-0.5">
                    {result.packages.map((pkg) => {
                      const image = pkg.featured_image?.url
                        ? resolveMediaUrl(pkg.featured_image.url)
                        : null;
                      return (
                        <li key={pkg.id}>
                          <Link
                            href={`${packagesPath}/${pkg.slug}`}
                            onClick={() => {
                              close();
                              onNavigate?.();
                            }}
                            className="-m-2 flex items-center gap-4 rounded p-2 hover:bg-bg"
                          >
                            <span className="relative block h-[70px] w-[70px] shrink-0 overflow-hidden rounded bg-bg">
                              {image ? (
                                <Image
                                  src={image}
                                  alt={pkg.featured_image?.alt || pkg.title}
                                  fill
                                  sizes="70px"
                                  className="object-cover"
                                />
                              ) : null}
                            </span>
                            <span className="min-w-0">
                              <span className="line-clamp-2 block text-sm font-bold text-headings">
                                {pkg.title}
                              </span>
                              <span className="mt-1 flex items-center gap-1 text-xs text-muted">
                                {String(pkg.duration).padStart(2, "0")}{" "}
                                {pkg.duration_unit}
                                {pkg.total_review ? (
                                  <>
                                    <span className="opacity-50">|</span>
                                    {String(pkg.total_review).padStart(
                                      2,
                                      "0",
                                    )}{" "}
                                    reviews
                                  </>
                                ) : null}
                              </span>
                            </span>
                          </Link>
                        </li>
                      );
                    })}
                  </ul>
                ) : (
                  <p className="p-4 text-center text-sm text-muted">
                    No trips found for &ldquo;{query}&rdquo;.
                  </p>
                )}
              </div>
            ) : null}
          </div>
        </div>
      ) : null}
    </>
  );
}
